import { FC, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@components/layout/DashboardLayout';
import { Button } from '@components/common/Button';
import { Modal } from '@components/feedback/Modal';
import { PortfolioSummary } from '@components/portfolio/PortfolioSummary';
import { PortfolioTable } from '@components/portfolio/PortfolioTable';
import { PortfolioPropertyForm } from '@components/portfolio/PortfolioPropertyForm';
import { PortfolioProperty, PortfolioPropertyFormData, PortfolioSummary as PortfolioSummaryType } from '@/types/index';
import { portfolioService } from '@services/portfolio.service';

const EMPTY_SUMMARY: PortfolioSummaryType = {
  propertyCount: 0,
  totalMarketValue: 0,
  totalDebt: 0,
  totalEquity: 0,
  totalNOI: 0,
  totalCashFlow: 0,
  globalLTV: null,
  avgCapRate: null,
};

export const Portfolio: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [properties, setProperties] = useState<PortfolioProperty[]>([]);
  const [summary, setSummary] = useState<PortfolioSummaryType>(EMPTY_SUMMARY);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState<PortfolioProperty | undefined>(undefined);
  const [prefillData, setPrefillData] = useState<PortfolioPropertyFormData | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const load = async () => {
    try {
      setIsLoading(true);
      const data = await portfolioService.getAll();
      setProperties(data.properties);
      setSummary(data.summary);
    } catch (err) {
      console.error('Erreur chargement portefeuille:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Ouverture automatique du formulaire pré-rempli depuis "Mes propriétés"
  useEffect(() => {
    const prefill = location.state?.prefillFromProperty as PortfolioPropertyFormData | undefined;
    if (prefill) {
      setPrefillData(prefill);
      setEditingProperty(undefined);
      setIsFormOpen(true);
      // Nettoyer le state pour éviter la réouverture au retour arrière
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  const handleAdd = () => {
    setEditingProperty(undefined);
    setPrefillData(undefined);
    setIsFormOpen(true);
  };

  const handleEdit = (p: PortfolioProperty) => {
    setEditingProperty(p);
    setIsFormOpen(true);
  };

  const handleDelete = async (p: PortfolioProperty) => {
    if (!confirm(`Supprimer "${p.name}" du portefeuille ?`)) return;
    try {
      await portfolioService.delete(p.id);
      await load();
    } catch (err) {
      console.error('Erreur suppression:', err);
      alert('Erreur lors de la suppression');
    }
  };

  const handleSubmit = async (data: PortfolioPropertyFormData) => {
    setIsSubmitting(true);
    try {
      if (editingProperty) {
        await portfolioService.update(editingProperty.id, data);
      } else {
        await portfolioService.create(data);
      }
      setIsFormOpen(false);
      setEditingProperty(undefined);
      await load();
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      alert('Erreur lors de l\'enregistrement');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setIsFormOpen(false);
    setEditingProperty(undefined);
    setPrefillData(undefined);
  };

  return (
    <DashboardLayout title="Portefeuille">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Portefeuille</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Pilotage de vos actifs immobiliers — cash-flow, LTV, rendement
            </p>
          </div>
          <Button onClick={handleAdd} variant="primary">
            + Ajouter un actif
          </Button>
        </div>

        {/* KPIs portefeuille */}
        {(properties.length > 0 || !isLoading) && (
          <PortfolioSummary summary={summary} />
        )}

        {/* Tableau */}
        <PortfolioTable
          properties={properties}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAnalyze={(id) => navigate(`/analysis/${id}`)}
          isLoading={isLoading}
        />

        {/* Modal formulaire */}
        <Modal
          isOpen={isFormOpen}
          onClose={handleCancel}
          title={
            editingProperty
              ? `Modifier — ${editingProperty.name}`
              : prefillData
                ? `Ajouter au portefeuille — ${prefillData.name}`
                : 'Nouvel actif immobilier'
          }
          size="xl"
        >
          <PortfolioPropertyForm
            initial={editingProperty}
            prefill={prefillData}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
          />
        </Modal>
      </div>
    </DashboardLayout>
  );
};
